import React, { useState, useEffect, useRef } from 'react';

// SignatureModal component for capturing and saving a user's signature
const SignatureModal = ({ isOpen, onClose, onSave, index }) => {
  // Reference to the canvas element for drawing the signature
  const canvasRef = useRef(null);
  // State to track whether the user is currently drawing
  const [drawing, setDrawing] = useState(false);

  // Initialize and resize the canvas when the modal is opened
  useEffect(() => {
    if (isOpen) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Function to resize the canvas to match its display size
      const resizeCanvas = () => {
        canvas.width = canvas.offsetWidth; // Set canvas width to its visible width
        canvas.height = 200; // Set fixed height
        ctx.fillStyle = 'white'; // Set background color to white
        ctx.fillRect(0, 0, canvas.width, canvas.height); // Fill canvas with white background
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas); // Adjust canvas on window resize

      // Cleanup: remove resize event listener when modal closes or component unmounts
      return () => window.removeEventListener('resize', resizeCanvas);
    }
  }, [isOpen]);

  // Get the mouse or touch position relative to the canvas
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX; // Handle touch or mouse events
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left, // Adjust X coordinate relative to canvas
      y: clientY - rect.top, // Adjust Y coordinate relative to canvas
    };
  };

  // Start drawing when the user clicks or touches the canvas
  const startDraw = (e) => {
    setDrawing(true); // Enable drawing mode
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPosition(e);
    ctx.beginPath(); // Start a new drawing path
    ctx.moveTo(pos.x, pos.y); // Move to the starting position
    e.preventDefault(); // Prevent default browser behavior
  };

  // Draw on the canvas as the user moves the mouse or finger
  const draw = (e) => {
    if (!drawing) return; // Only draw if in drawing mode
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPosition(e);
    ctx.lineTo(pos.x, pos.y); // Draw line to current position
    ctx.stroke(); // Render the line
    e.preventDefault(); // Prevent default browser behavior
  };

  // Stop drawing when the user releases the mouse or touch
  const endDraw = (e) => {
    setDrawing(false); // Disable drawing mode
    e.preventDefault(); // Prevent default browser behavior
  };

  // Clear the canvas and reset to a white background
  const clearPad = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear the canvas
    ctx.fillStyle = 'white'; // Set background color to white
    ctx.fillRect(0, 0, canvas.width, canvas.height); // Refill with white background
  };

  // Save the signature as a PNG data URL and pass it to the parent component
  const handleSave = () => {
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png'); // Convert canvas to PNG data URL
    onSave(dataURL, index); // Call the provided onSave function with the data URL and index
    onClose(); // Close the modal
  };

  // Render nothing if the modal is not open
  if (!isOpen) return null;

  // Render the modal
  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Signature</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            {/* Canvas for drawing the signature */}
            <canvas
              ref={canvasRef}
              className="w-100 border-2 border-dashed border-gray-500 bg-white"
              style={{ touchAction: 'none' }} // Prevent default touch scrolling
              onMouseDown={startDraw} // Start drawing on mouse down
              onMouseMove={draw} // Draw on mouse move
              onMouseUp={endDraw} // Stop drawing on mouse up
              onTouchStart={startDraw} // Start drawing on touch start
              onTouchMove={draw} // Draw on touch move
              onTouchEnd={endDraw} // Stop drawing on touch end
            />
            {/* Clear button to reset the canvas */}
            <button
              type="button"
              className="mt-2 px-4 btn btn-primary py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={clearPad}
            >
              Clear
            </button>
          </div>
          <div className="modal-footer">
            {/* Save button to save the signature */}
            <button type="button" className="btn btn-success" onClick={handleSave}>
              Save
            </button>
            {/* Cancel button to close the modal */}
            <button
              type="button"
              className="btn bg-danger-subtle text-danger font-medium"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;